import { createContext, useContext, useEffect, useMemo, useState } from "react";

export type ThemeMode = "system" | "light" | "dark";
export type TextSize = "sm" | "md" | "lg" | "xl";

export interface DisplayPrefs {
  theme: ThemeMode;
  textSize: TextSize;
  highContrast: boolean;
  spacious: boolean;
  largeTargets: boolean;
}

const DEFAULTS: DisplayPrefs = {
  theme: "system",
  textSize: "md",
  highContrast: false,
  spacious: false,
  largeTargets: false,
};

const STORAGE_KEY = "rt:display-prefs:v1";

function loadPrefs(): DisplayPrefs {
  if (typeof window === "undefined") return DEFAULTS;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULTS;
    return { ...DEFAULTS, ...(JSON.parse(raw) as Partial<DisplayPrefs>) };
  } catch {
    return DEFAULTS;
  }
}

function applyPrefs(p: DisplayPrefs) {
  if (typeof document === "undefined") return;
  const root = document.documentElement;

  // Theme
  const systemDark =
    typeof window !== "undefined" &&
    window.matchMedia("(prefers-color-scheme: dark)").matches;
  const isDark = p.theme === "dark" || (p.theme === "system" && systemDark);
  root.classList.toggle("dark", isDark);

  // Text size — strip then add
  root.classList.remove("rt-text-sm", "rt-text-md", "rt-text-lg", "rt-text-xl");
  root.classList.add(`rt-text-${p.textSize}`);

  root.classList.toggle("rt-hc", p.highContrast);
  root.classList.toggle("rt-spacious", p.spacious);
  root.classList.toggle("rt-big-tap", p.largeTargets);
}

interface Ctx {
  prefs: DisplayPrefs;
  setPrefs: (p: Partial<DisplayPrefs>) => void;
  reset: () => void;
}

const DisplayCtx = createContext<Ctx | null>(null);

export function DisplaySettingsProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [prefs, setPrefsState] = useState<DisplayPrefs>(DEFAULTS);

  // Hydrate from localStorage on mount
  useEffect(() => {
    const loaded = loadPrefs();
    setPrefsState(loaded);
    applyPrefs(loaded);
  }, []);

  // Re-apply on changes
  useEffect(() => {
    applyPrefs(prefs);
  }, [prefs]);

  // Track system theme when in "system" mode
  useEffect(() => {
    if (prefs.theme !== "system" || typeof window === "undefined") return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = () => applyPrefs(prefs);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, [prefs]);

  const value = useMemo<Ctx>(
    () => ({
      prefs,
      setPrefs: (patch) =>
        setPrefsState((cur) => {
          const next = { ...cur, ...patch };
          try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
          } catch {}
          return next;
        }),
      reset: () => {
        try {
          localStorage.removeItem(STORAGE_KEY);
        } catch {}
        setPrefsState(DEFAULTS);
      },
    }),
    [prefs],
  );

  return <DisplayCtx.Provider value={value}>{children}</DisplayCtx.Provider>;
}

export function useDisplaySettings(): Ctx {
  const ctx = useContext(DisplayCtx);
  if (!ctx)
    throw new Error("useDisplaySettings must be used inside DisplaySettingsProvider");
  return ctx;
}

/**
 * Inline boot snippet to apply the theme class before first paint and avoid
 * a flash of light theme on dark-mode users. Include as a script in <head>.
 */
export const DISPLAY_BOOT_SCRIPT = `(function(){try{var s=localStorage.getItem('${STORAGE_KEY}');var p=s?JSON.parse(s):{};var t=p.theme||'system';var sysDark=window.matchMedia('(prefers-color-scheme: dark)').matches;var dark=t==='dark'||(t==='system'&&sysDark);var r=document.documentElement;if(dark)r.classList.add('dark');r.classList.add('rt-text-'+(p.textSize||'md'));if(p.highContrast)r.classList.add('rt-hc');if(p.spacious)r.classList.add('rt-spacious');if(p.largeTargets)r.classList.add('rt-big-tap');}catch(e){}})();`;
